/**
 * Image Compressor Service
 * Compress images with adjustable quality settings
 */

export interface CompressionResult {
  originalSize: number;
  compressedSize: number;
  savedPercentage: number;
  blob: Blob;
  dataUrl: string;
  format: string;
}

export interface CompressionOptions {
  quality: number; // 0-1
  maxWidth?: number;
  maxHeight?: number;
  format?: 'jpeg' | 'png' | 'webp';
}

/**
 * Compress an image file
 */
export async function compressImage(
  file: File,
  options: CompressionOptions
): Promise<CompressionResult> {
  const { quality = 0.8, maxWidth, maxHeight, format } = options;
  
  const img = await loadImage(file);
  
  // Determine output format
  let outputFormat = format;
  if (!outputFormat) {
    if (file.type === 'image/png') outputFormat = 'png';
    else if (file.type === 'image/webp') outputFormat = 'webp';
    else outputFormat = 'jpeg';
  }
  
  // Calculate new dimensions
  let { width, height } = img;
  
  if (maxWidth && width > maxWidth) {
    height = Math.round((height * maxWidth) / width);
    width = maxWidth;
  }
  
  if (maxHeight && height > maxHeight) {
    width = Math.round((width * maxHeight) / height);
    height = maxHeight;
  }
  
  // Create canvas and compress
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  if (!ctx) throw new Error('Canvas not supported');
  
  canvas.width = width;
  canvas.height = height;
  
  // Enable high-quality scaling
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  
  // Draw image
  ctx.drawImage(img, 0, 0, width, height);
  
  // Get compressed blob
  const mimeType = `image/${outputFormat}`;
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (b) resolve(b);
        else reject(new Error('Failed to compress image'));
      },
      mimeType,
      quality
    );
  });
  
  // Calculate savings
  const originalSize = file.size;
  const compressedSize = blob.size;
  const savedPercentage = Math.round(((originalSize - compressedSize) / originalSize) * 100);
  
  return {
    originalSize,
    compressedSize,
    savedPercentage,
    blob,
    dataUrl: await blobToDataUrl(blob),
    format: outputFormat,
  };
}

/**
 * Load image from file
 */
function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Convert blob to data URL
 */
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to convert blob'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}
